import type {
  TwilioError,
  LocalAudioTrack as TwilioLocalAudioTrack,
  NetworkQualityLevel as TwilioNetworkQualityLevel,
  NetworkQualityStats as TwilioNetworkQualityStats,
  RemoteAudioTrack as TwilioRemoteAudioTrack,
  RemoteDataTrack as TwilioRemoteDataTrack,
  RemoteTrack as TwilioRemoteTrack,
  RemoteTrackPublication as TwilioRemoteTrackPublication,
  RemoteVideoTrack as TwilioRemoteVideoTrack,
} from 'twilio-video';

import type { RemoteMediaDelegator } from '../../../core/room/participant/media/MediaDelegator';
import type { RemoteParticipantEvent, RemoteParticipantEventMap } from '../../../core/room/participant/types';
import { handleUnhandledEventHandlerException } from '../../../core/utils';
import type { StandardEventToTwilioEventMap } from './events';
import { STANDARD_EVENT_TO_TWILIO_EVENT_MAP } from './events';
import { TwilioLocalAudioDelegator } from './media/LocalAudioDelegator';
import { TwilioRemoteAudioDelegator } from './media/RemoteAudioDelegator';
import { TwilioRemoteDataDelegator } from './media/RemoteDataDelegator';
import { TwilioRemoteVideoDelegator } from './media/RemoteVideoDelegator';

export function isEventSupported(event: string): event is RemoteParticipantEvent {
  return event in STANDARD_EVENT_TO_TWILIO_EVENT_MAP;
}

export function getTwilioEvent<T extends RemoteParticipantEvent>(
  standardEvent: T,
): StandardEventToTwilioEventMap[T] | undefined {
  return STANDARD_EVENT_TO_TWILIO_EVENT_MAP[standardEvent];
}

function createDelegatorFromTrack(track: TwilioRemoteTrack): RemoteMediaDelegator {
  const kind = track.kind;
  switch (kind) {
    case 'audio':
      return new TwilioRemoteAudioDelegator(track as TwilioRemoteAudioTrack);
    case 'video':
      return new TwilioRemoteVideoDelegator(track as TwilioRemoteVideoTrack);
    case 'data':
      return new TwilioRemoteDataDelegator(track as TwilioRemoteDataTrack);
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function createLocalAudioDelegator(track: TwilioLocalAudioTrack): TwilioLocalAudioDelegator {
  return new TwilioLocalAudioDelegator(track);
}

function coercePublicationIntoDelegator(publication: TwilioRemoteTrackPublication): RemoteMediaDelegator {
  const { kind } = publication;
  const track = publication.track || { kind };
  switch (kind) {
    case 'audio':
      return new TwilioRemoteAudioDelegator(track as TwilioRemoteAudioTrack);
    case 'video':
      return new TwilioRemoteVideoDelegator(track as TwilioRemoteVideoTrack);
    case 'data':
      return new TwilioRemoteDataDelegator(track as TwilioRemoteDataTrack);
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

function wrapAudioVideoEventHandler(handler: RemoteParticipantEventMap['mediaSubscribed']) {
  return function twilioAudioVideoHandler(track: TwilioRemoteTrack, _publication: TwilioRemoteTrackPublication) {
    const delegator = createDelegatorFromTrack(track);
    const result = handler(delegator);
    handleUnhandledEventHandlerException(result, 'Twilio: trackSubscribed');
  };
}

function wrapAudioVideoToggleEventHandler(handler: RemoteParticipantEventMap['mediaEnabled']) {
  return function twilioAudioVideoToggleHandler(publication: TwilioRemoteTrackPublication) {
    // application code only expects publication.kind
    const delegator = coercePublicationIntoDelegator(publication);
    handler(delegator);
  };
}

function wrapAudioVideoEventFailureHandler(handler: RemoteParticipantEventMap['mediaSubscriptionFailed']) {
  return function twilioAudioVideoFailureHandler(error: TwilioError, publication: TwilioRemoteTrackPublication) {
    const delegator = coercePublicationIntoDelegator(publication);
    handler(error, delegator);
  };
}

// this wrapper will remain pass-through as Zoom doesn't have an equivalent
function wrapNetworkQualityEventHandler(handler: RemoteParticipantEventMap['networkQualityLevelChanged']) {
  return function twilioNetworkQualityHandler(level: TwilioNetworkQualityLevel, stats: TwilioNetworkQualityStats) {
    handler(level, stats);
  };
}

/**
 * Create a Twilio event handler wrapper with proper type safety.
 *
 * Ideally we would not have type assertions but this was the easiest approach.
 * In alternative approache, the complex mapping types ran into Typescript's limitations:
 * - Loss of key–value correlation
 * - Intersections of incompatible tuples collapse to never
 */
export function createTwilioEventHandler<K extends RemoteParticipantEvent>(
  event: K,
  handler: RemoteParticipantEventMap[K],
) {
  switch (event) {
    case 'mediaSubscribed':
    case 'mediaUnsubscribed':
    case 'mediaSwitchedOff':
    case 'mediaSwitchedOn':
      return wrapAudioVideoEventHandler(handler as RemoteParticipantEventMap['mediaSubscribed']);

    case 'mediaEnabled':
      return wrapAudioVideoToggleEventHandler(handler as RemoteParticipantEventMap['mediaEnabled']);

    case 'mediaSubscriptionFailed':
      return wrapAudioVideoEventFailureHandler(handler as RemoteParticipantEventMap['mediaSubscriptionFailed']);

    case 'networkQualityLevelChanged':
      return wrapNetworkQualityEventHandler(handler as RemoteParticipantEventMap['networkQualityLevelChanged']);

    default: {
      const _exhaustiveCheck: never = event;
      throw new Error(`Unsupported event: ${_exhaustiveCheck}`);
    }
  }
}
